import { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { BundleUpsell } from "@/components/BundleUpsell";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { SEOHead } from "@/components/SEOHead";
import { useCartStore, syncTrackingAttributesToCart } from "@/stores/cartStore";
import { useLanguage } from "@/lib/i18n";
import { fetchProductByHandle, formatPrice, calcDiscountPercent, createBuyNowCart, CURRENCY_TO_COUNTRY } from "@/lib/shopify";
import { getFixedPrice, formatFixedPrice, FREE_SHIPPING_THRESHOLDS } from "@/lib/fixedPrices";
import { useTranslatedProductContent } from "@/hooks/useTranslatedProductContent";
import { trackViewContent, trackInitiateCheckout, generateEventId } from "@/lib/facebookPixel";
import { trackGA4ViewContent, trackGA4InitiateCheckout } from "@/lib/googleAnalytics";
import { getProductImages } from "@/lib/productImages";
import paymentMethodsImg from "@/assets/payment-methods.png";
import { ProductImageGallery } from "@/components/product/ProductImageGallery";
import { CustomerReviews, BeforeAfterSection } from "@/components/product/CustomerReviews";
import { HowToUseSection } from "@/components/product/HowToUse";
import { IngredientsSection } from "@/components/product/Ingredients";
import { ResultsTimelineSection } from "@/components/product/ResultsTimeline";
import { ScienceInside } from "@/components/product/ScienceInside";
import { WhatIsPDRN } from "@/components/product/WhatIsPDRN";
import { ProvenResults } from "@/components/product/ProvenResults";
import { ComparisonTable } from "@/components/product/ComparisonTable";
import { ProductFAQ } from "@/components/product/ProductFAQ";
import { MobileStickyCart } from "@/components/product/MobileStickyCart";
import { RelatedProducts } from "@/components/product/RelatedProducts";
import { RelatedArticles } from "@/components/blog/RelatedArticles";
import { ShippingMarquee } from "@/components/product/ShippingMarquee";
import { 
  ShoppingCart, 
  Loader2, 
  ArrowLeft, 
  Minus,
  Plus,
  Star,
  Droplets,
  Sparkles,
  Zap,
  Shield,
  CreditCard,
  Truck,
  FlaskConical,
  Award,
  Check,
  Leaf,
  Heart,
  Package,
  Clock,
  ShieldCheck,
} from "lucide-react";

// Before/After images
import hannahBefore from "@/assets/before-after/hannah-before.png";
import hannahAfter from "@/assets/before-after/hannah-after.jpeg";
import anitaBefore from "@/assets/before-after/anita-before.png";
import anitaAfter from "@/assets/before-after/anita-after.png";
import latanishaBefore from "@/assets/before-after/latanisha-before.png";
import latanishaAfter from "@/assets/before-after/latanisha-after.png";


export default function ProductPage() {
  const { handle } = useParams<{ handle: string }>();
  const { t, currency } = useLanguage();
  const addItem = useCartStore(state => state.addItem);
  const isLoading = useCartStore(state => state.isLoading);
  
  const [product, setProduct] = useState<Awaited<ReturnType<typeof fetchProductByHandle>>>(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [isBuyingNow, setIsBuyingNow] = useState(false);
  const viewContentTracked = useRef(false);

  // Get translated product-specific content - must be called before any early returns
  const productContent = useTranslatedProductContent(handle || '');

  useEffect(() => {
    const loadProduct = async () => {
      if (!handle) return;
      setLoading(true);
      viewContentTracked.current = false;
      try {
        const data = await fetchProductByHandle(handle);
        setProduct(data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    loadProduct();
  }, [handle]);

  // Track ViewContent when product loads
  useEffect(() => {
    if (product && !viewContentTracked.current) {
      const variant = product.variants.edges[0]?.node;
      if (variant) {
        viewContentTracked.current = true;
        // Use fixed display price + currency so tracking matches what the user sees
        const displayCur = currency || 'USD';
        const fixedP = getFixedPrice(handle || '', displayCur);
        const viewContentData = {
          id: product.id,
          title: product.title,
          price: fixedP ? fixedP.sale : parseFloat(variant.price.amount),
          currency: fixedP ? displayCur : variant.price.currencyCode,
          category: product.productType || undefined,
        };
        // Generate ONE shared event_id for both FB and GA4 — ensures dashboard dedup
        (async () => {
          const eventId = await generateEventId('ViewContent', viewContentData.id);
          await Promise.all([
            trackViewContent(viewContentData, eventId),
            trackGA4ViewContent(viewContentData, eventId),
          ]);
        })();
      }
    }
  }, [product, currency, handle]);

  if (loading) {
    return (
      <div className="min-h-screen bg-section-gradient">
        <Header />
        <main className="container px-4 py-6 md:py-8">
          <div className="grid lg:grid-cols-2 gap-6 md:gap-10">
            {/* Image skeleton */}
            <div>
              <div className="aspect-square rounded-2xl bg-muted animate-pulse" />
              <div className="flex gap-2 mt-4">
                {[1,2,3].map(i => <div key={i} className="w-16 h-16 md:w-20 md:h-20 rounded-lg bg-muted animate-pulse" />)}
              </div>
            </div>
            {/* Info skeleton */}
            <div className="space-y-4">
              <div className="h-6 w-24 rounded bg-muted animate-pulse" />
              <div className="h-10 w-3/4 rounded bg-muted animate-pulse" />
              <div className="h-5 w-1/3 rounded bg-muted animate-pulse" />
              <div className="h-12 w-1/2 rounded bg-muted animate-pulse" />
              <div className="h-20 w-full rounded bg-muted animate-pulse" />
              <div className="h-12 w-full rounded-lg bg-muted animate-pulse" />
              <div className="h-12 w-full rounded-lg bg-muted animate-pulse" />
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-section-gradient">
        <Header />
        <main className="container py-20 text-center">
          <h1 className="text-2xl md:text-3xl font-extrabold mb-4">{t('product.notFound')}</h1>
          <Button asChild>
            <Link to="/">
              <ArrowLeft className="w-4 h-4 me-2" />
              {t('product.backHome')}
            </Link>
          </Button>
        </main>
        <Footer />
      </div>
    );
  }

  const variant = product.variants.edges[0]?.node;
  
  // Merge local images with Shopify images
  const localImages = getProductImages(handle || '');
  const localEdges = localImages.map(img => ({
    node: {
      url: img.src,
      altText: t(img.altKey),
    },
  }));
  const shopifyEdges = product.images?.edges || [];
  const mergedImages = { edges: [...localEdges, ...shopifyEdges] };
  
  const isBundle = product.tags.includes('bundle');
  const displayCurrency = currency || 'USD';
  const fp = getFixedPrice(handle || '', displayCurrency);
  const isOnSale = fp ? fp.original > fp.sale : false;
  const discountPercent = fp ? calcDiscountPercent(fp.sale, fp.original) : 0;

  const handleAddToCart = async () => {
    if (!variant) return;
    
    await addItem({
      product: { node: product },
      variantId: variant.id,
      variantTitle: variant.title,
      price: variant.price,
      quantity,
      selectedOptions: variant.selectedOptions || []
    });
  };

  const handleBuyNow = async () => {
    if (!variant || isBuyingNow) return;
    setIsBuyingNow(true);
    // Open blank window immediately to preserve user-gesture context (prevents popup blocking)
    const newWindow = window.open('about:blank', '_blank');
    try {
      const result = await createBuyNowCart(variant.id, quantity, CURRENCY_TO_COUNTRY[currency] || 'US');
      if (result) {
        // Final sync of tracking attributes before checkout redirect
        await syncTrackingAttributesToCart(result.cartId);

        // Fire InitiateCheckout tracking (CAPI + GA4) before redirect
        const price = fp?.sale || parseFloat(variant.price.amount);
        const checkoutData = {
          items: [{ productId: product.id, variantId: variant.id, title: product.title, price, quantity }],
          totalValue: price * quantity,
          currency: displayCurrency,
        };
        // Generate ONE shared event_id for both FB and GA4 — ensures dashboard dedup
        const icEventId = await generateEventId('InitiateCheckout', Date.now().toString());
        await Promise.all([
          trackInitiateCheckout(checkoutData, icEventId),
          trackGA4InitiateCheckout(checkoutData, icEventId),
        ]);

        if (newWindow) {
          newWindow.location.href = result.checkoutUrl;
        } else {
          window.location.href = result.checkoutUrl;
        }
      } else {
        newWindow?.close();
        toast.error(t('cart.error') || 'Something went wrong');
      }
    } catch (err) {
      console.error('Buy Now error:', err);
      newWindow?.close();
      toast.error(t('cart.error') || 'Something went wrong');
    } finally {
      setIsBuyingNow(false);
    }
  };

  // Product benefits 
  const benefits = [
    { icon: Droplets, text: t('feature.hydration'), color: "text-blue-500" },
    { icon: Sparkles, text: t('feature.glassSkin'), color: "text-primary" },
    { icon: Zap, text: t('feature.antiAging'), color: "text-amber-500" },
    { icon: Shield, text: t('feature.collagen'), color: "text-emerald-500" },
  ];

  // Before/After with real images and translated names
  const beforeAfterItems = [
    { name: t('beforeAfter.customer1'), duration: t('beforeAfter.weeks3'), beforeImage: hannahBefore, afterImage: hannahAfter },
    { name: t('beforeAfter.customer2'), duration: t('beforeAfter.weeks8'), beforeImage: anitaBefore, afterImage: anitaAfter },
    { name: t('beforeAfter.customer3'), duration: t('beforeAfter.weeks12'), beforeImage: latanishaBefore, afterImage: latanishaAfter },
  ];

  // Build breadcrumbs for SEO
  const breadcrumbs = [
    { name: t('nav.home'), url: 'https://pdrn-skin.com/' },
    { name: t('nav.shop'), url: 'https://pdrn-skin.com/#products' },
    { name: productContent.title || product.title },
  ];

  // Build HowTo steps for SEO
  const howToSteps = productContent.howToUse.map(step => ({
    name: step.title,
    text: step.description,
  }));

  return (
    <div className="min-h-screen bg-section-gradient">
      <SEOHead
        title={t(`seo.productTitle.${handle}`) !== `seo.productTitle.${handle}` ? t(`seo.productTitle.${handle}`) : `${productContent.title || product.title} | PDRN Skincare`}
        description={t(`seo.productDescription.${handle}`) !== `seo.productDescription.${handle}` ? t(`seo.productDescription.${handle}`) : (productContent.description || product.description)}
        image={mergedImages.edges[0]?.node?.url}
        url={`https://pdrn-skin.com/product/${handle}`}
        type="product"
        breadcrumbs={breadcrumbs}
        howToSteps={howToSteps}
        faqItems={productContent.faq?.map(f => ({ question: f.question, answer: f.answer }))}
        product={variant ? {
          name: productContent.title || product.title,
          price: variant.price.amount,
          currency: variant.price.currencyCode,
          availability: variant.availableForSale,
          sku: handle,
          rating: 4.9,
          reviewCount: 127,
          image: mergedImages.edges[0]?.node?.url,
          description: productContent.description || product.description,
          ...(fp ? { highPrice: fp.original.toString() } : {}),
          localPrice: fp?.sale,
          localCurrency: fp ? displayCurrency : undefined,
          reviews: productContent.reviews.slice(0, 3).map(r => ({
            author: r.name,
            rating: r.rating,
            body: r.text,
          })),
        } : undefined}
      />
      <Header />
      
      <main className="container px-4 py-6 md:py-8" aria-label="Product details">
        {/* Breadcrumb */}
        <nav className="mb-6 md:mb-8" aria-label="Breadcrumb">
          <ol className="flex items-center gap-2 text-xs md:text-sm text-muted-foreground overflow-x-auto" itemScope itemType="https://schema.org/BreadcrumbList">
            <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem" className="flex-shrink-0">
              <Link to="/" className="hover:text-primary transition-colors" itemProp="item"><span itemProp="name">{t('nav.home')}</span></Link>
              <meta itemProp="position" content="1" />
            </li>
            <li aria-hidden="true">/</li>
            <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem" className="flex-shrink-0">
              <Link to="/#products" className="hover:text-primary transition-colors" itemProp="item"><span itemProp="name">{t('nav.shop')}</span></Link>
              <meta itemProp="position" content="2" />
            </li>
            <li aria-hidden="true">/</li>
            <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem" className="text-foreground font-medium truncate max-w-[150px] md:max-w-none">
              <span itemProp="name">{productContent.title || product.title}</span>
              <meta itemProp="position" content="3" />
            </li>
          </ol>
        </nav>

        {/* Main Product Section */}
        <div className="grid lg:grid-cols-2 gap-6 md:gap-10 mb-8 md:mb-10">
          {/* Image Gallery */}
          <ProductImageGallery images={mergedImages} productTitle={product.title} />

          {/* Product Info */}
          <div>
            {/* Badges */}
            <div className="flex flex-wrap gap-2 mb-3 md:mb-4">
              {isBundle && (
                <Badge className="bg-primary text-primary-foreground border-0 text-xs md:text-sm">
                  {t('product.bestValue')}
                </Badge>
              )}
              {isOnSale && discountPercent > 0 && (
                <Badge className="bg-destructive text-destructive-foreground border-0 text-xs md:text-sm">
                  -{discountPercent}%
                </Badge>
              )}
              {product.tags.includes('bestseller') && (
                <Badge variant="secondary" className="text-xs md:text-sm">{t('product.bestseller')}</Badge>
              )}
            </div>

            <h1 className="text-2xl md:text-3xl lg:text-4xl font-extrabold mb-3 md:mb-4 text-foreground">
              {productContent.title || product.title}
            </h1>

            {/* Rating */}
            <div className="flex items-center gap-2 mb-3 md:mb-4">
              <div className="flex">
                {[1,2,3,4,5].map(i => (
                  <Star key={i} className="w-4 h-4 md:w-5 md:h-5 fill-amber-400 text-amber-400" />
                ))}
              </div>
              <span className="text-xs md:text-sm text-muted-foreground">{t('product.reviewCount', { count: 127 })}</span>
            </div>

            {/* Stock indicator */}
            <div className="flex items-center gap-2 mb-3 md:mb-4">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-success opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-success"></span>
              </span>
              <span className="text-xs md:text-sm font-medium text-success">{t('product.inStockReady')}</span>
            </div>

            {/* Price */}
            <div className="flex items-center gap-2 md:gap-3 mb-4 md:mb-6">
              {fp ? (
                <>
                  <span className="text-3xl md:text-4xl font-extrabold text-foreground">
                    {formatFixedPrice(fp.sale, displayCurrency)}
                  </span>
                  {isOnSale && (
                    <span className="text-base md:text-lg font-light text-muted-foreground line-through opacity-60">
                      {formatFixedPrice(fp.original, displayCurrency)}
                    </span>
                  )}
                </>
              ) : (
                <span className="text-3xl md:text-4xl font-extrabold text-foreground">
                  {variant && formatPrice(variant.price.amount, variant.price.currencyCode, currency)}
                </span>
              )}
            </div>

            {/* Description */}
            <p className="text-sm md:text-base text-muted-foreground mb-4 md:mb-6 leading-relaxed product-description">
              {productContent.description || product.description}
            </p>

            {/* Product Specs Strip */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 mb-4 md:mb-6 p-3 md:p-4 bg-white/60 rounded-xl border border-border/50">
              <div className="flex items-center gap-2">
                <Package className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                <span className="text-xs text-muted-foreground whitespace-nowrap">{t('product.specsVolume')}:</span>
                <span className="text-xs font-medium">{productContent.specs.volume}</span>
              </div>
              <div className="flex items-center gap-2">
                <FlaskConical className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                <span className="text-xs text-muted-foreground whitespace-nowrap">{t('product.specsTested')}:</span>
                <span className="text-xs font-medium">{t('product.specsClinicallyTested')}</span>
              </div>
              <div className="flex items-center gap-2">
                <Heart className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                <span className="text-xs text-muted-foreground whitespace-nowrap">{t('product.specsSkinType')}:</span>
                <span className="text-xs font-medium">{productContent.specs.skinType}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                <span className="text-xs text-muted-foreground whitespace-nowrap">{t('product.specsShelfLife')}:</span>
                <span className="text-xs font-medium">{productContent.specs.shelfLife}</span>
              </div>
            </div>

            {/* Benefits Grid */}
            <div className="grid grid-cols-2 gap-2 md:gap-3 mb-4 md:mb-6">
              {benefits.map((benefit, idx) => (
                <div key={idx} className="flex items-center gap-2 md:gap-3 p-2 md:p-3 bg-white/60 rounded-xl border border-white/80">
                  <benefit.icon className={`w-4 h-4 md:w-5 md:h-5 ${benefit.color} flex-shrink-0`} />
                  <span className="text-xs md:text-sm font-medium">{benefit.text}</span>
                </div>
              ))}
            </div>

            {/* Highlight Bullets */}
            <div className="mb-4 md:mb-6 space-y-1.5">
              {productContent.highlights.map((highlight, idx) => (
                <div key={idx} className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-success flex-shrink-0 mt-0.5" />
                  <span className="text-xs md:text-sm text-foreground/80">{highlight.text}</span>
                </div>
              ))}
            </div>

            {/* Quantity Selector */}
            <div className="flex items-center gap-3 md:gap-4 mb-4 md:mb-6">
              <span className="text-xs md:text-sm font-medium">{t('products.quantity')}:</span>
              <div className="flex items-center border border-border rounded-lg bg-white/60">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 md:h-10 md:w-10"
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  disabled={quantity <= 1}
                >
                  <Minus className="w-3 h-3 md:w-4 md:h-4" />
                </Button>
                <span className="w-8 md:w-12 text-center text-sm md:text-base font-medium">{quantity}</span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 md:h-10 md:w-10"
                  onClick={() => setQuantity(quantity + 1)}
                >
                  <Plus className="w-3 h-3 md:w-4 md:h-4" />
                </Button>
              </div>
            </div>

            {/* Add to Cart */}

            {/* Satisfaction Guarantee Badge */}
            <div className="flex items-center gap-2.5 p-3 mb-3 md:mb-4 bg-emerald-50/60 rounded-xl border border-emerald-200/50">
              <ShieldCheck className="w-5 h-5 text-emerald-600 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-xs md:text-sm font-semibold text-emerald-800">{t('product.satisfactionGuarantee')}</p>
                <p className="text-[10px] md:text-xs text-emerald-600/80">{t('product.satisfactionDesc')}</p>
              </div>
            </div>

            {/* Add to Cart */}
            <div className="space-y-2 mb-3 md:mb-4">
              <Button
                size="lg"
                onClick={handleAddToCart}
                disabled={isLoading || !variant?.availableForSale}
                className="w-full bg-primary hover:bg-primary/90 text-primary-foreground text-sm md:text-lg py-5 md:py-6"
              >
                {isLoading ? (
                  <Loader2 className="w-4 h-4 md:w-5 md:h-5 animate-spin" />
                ) : !variant?.availableForSale ? (
                  t('products.soldOut')
                ) : (
                  <>
                    <ShoppingCart className="w-4 h-4 md:w-5 md:h-5 me-2" />
                    {t('products.addToCart')}
                  </>
                )}
              </Button>

              {/* Buy Now */}
              <Button
                size="lg"
                variant="outline"
                onClick={handleBuyNow}
                disabled={isBuyingNow || !variant?.availableForSale}
                className="w-full border-primary text-primary hover:bg-primary/10 text-sm md:text-lg py-5 md:py-6"
              >
                {isBuyingNow ? (
                  <Loader2 className="w-4 h-4 md:w-5 md:h-5 animate-spin" />
                ) : (
                  <>
                    <CreditCard className="w-4 h-4 md:w-5 md:h-5 me-2" />
                    {t('products.buyNow')}
                  </>
                )}
              </Button>
            </div>

            {/* Terms agreement */}
            <p className="text-[10px] text-muted-foreground/70 text-center leading-tight mb-3 md:mb-4">
              {t('product.termsAgreementBefore')} {t('product.termsLink')}{t('product.termsAgreementAfter') ? ` ${t('product.termsAgreementAfter')}` : ''}
            </p>

            {/* Trust badges — enhanced */}
            <div className="grid grid-cols-3 gap-2 mb-3">
              <div className="flex items-center gap-1.5 p-2 bg-white/60 rounded-xl border border-border/50">
                <Shield className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                <span className="text-[10px] md:text-xs font-medium">{t('product.secureCheckout')}</span>
              </div>
              <div className="flex items-center gap-1.5 p-2 bg-white/60 rounded-xl border border-border/50">
                <Truck className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                <span className="text-[10px] md:text-xs font-medium">{t('product.freeShipping', { amount: formatFixedPrice(FREE_SHIPPING_THRESHOLDS[displayCurrency] || FREE_SHIPPING_THRESHOLDS.USD, displayCurrency) })}</span>
              </div>
              <div className="flex items-center gap-1.5 p-2 bg-white/60 rounded-xl border border-border/50">
                <FlaskConical className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                <span className="text-[10px] md:text-xs font-medium">{t('product.koreanFormula')}</span>
              </div>
              <div className="flex items-center gap-1.5 p-2 bg-white/60 rounded-xl border border-border/50">
                <Award className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                <span className="text-[10px] md:text-xs font-medium">{t('product.moneyBack')}</span>
              </div>
              <div className="flex items-center gap-1.5 p-2 bg-white/60 rounded-xl border border-border/50">
                <Leaf className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                <span className="text-[10px] md:text-xs font-medium">{t('product.crueltyFree')}</span>
              </div>
              <div className="flex items-center gap-1.5 p-2 bg-white/60 rounded-xl border border-border/50">
                <Heart className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                <span className="text-[10px] md:text-xs font-medium">{t('product.dermatologistTested')}</span>
              </div>
            </div>




            {/* Payment methods image */}
            <div className="flex justify-center mt-4 mb-2 md:mt-6 md:mb-3">
              <img 
                src={paymentMethodsImg} 
                alt="Accepted payment methods: Shop Pay, Visa, Mastercard, American Express, Diners Club, Discover, Apple Pay, Google Pay, PayPal" 
                className="w-full max-w-sm object-contain"
                loading="lazy"
              />
            </div>

          </div>
        </div>


        {/* How to Use Section */}
        <HowToUseSection steps={productContent.howToUse} />

        {/* Key Ingredients Section */}
        <IngredientsSection ingredients={productContent.ingredients} />

        {/* What is PDRN — educational section (all products) */}
        <WhatIsPDRN />

        {/* Science Inside Section — deep dive for all products */}
        <ScienceInside handle={handle || ''} />

        {/* Results Timeline Section */}
        <ResultsTimelineSection items={productContent.resultsTimeline} />

        {/* Proven Results — statistics strip */}
        <ProvenResults />

        {/* PDRN vs Regular Skincare — comparison table */}
        <ComparisonTable />

        {/* Before/After Section */}
        <BeforeAfterSection 
          items={beforeAfterItems} 
          title={t('product.beforeAfter')}
        />

        {/* Product FAQ Section */}
        {productContent.faq.length > 0 && (
          <ProductFAQ items={productContent.faq} />
        )}

        {/* Customer Reviews Section */}
        <CustomerReviews reviews={productContent.reviews} />

        {/* Bundle Upsell — after social proof, before related products */}
        {!isBundle && (
          <BundleUpsell />
        )}

        {/* Related Products */}
        <RelatedProducts currentHandle={handle || ''} />

        {/* Related Blog Articles — internal linking for SEO authority */}
        {(() => {
          const productToArticles: Record<string, string[]> = {
            'pink-glow-serum-with-pdrn-peptides': ['pdrn-serum-salmon-dna-benefits', 'how-to-achieve-glass-skin-with-pdrn'],
            'pdrn-pink-sheet-masks-10-pack': ['how-to-achieve-glass-skin-with-pdrn', 'korean-skincare-secret-pdrn'],
            'smart-capsule-cream-with-collagen': ['pdrn-vs-traditional-skincare', 'what-is-pdrn-salmon-dna-skincare'],
            'complete-pdrn-trio-bundle': ['korean-skincare-secret-pdrn', 'pdrn-vs-traditional-skincare'],
          };
          const slugs = productToArticles[handle || ''] || [];
          return slugs.length > 0 ? <RelatedArticles slugs={slugs} /> : null;
        })()}

        {/* Explore PDRN Skincare — keyword-rich internal links for SEO */}
        <section className="mt-12 mb-8 p-6 bg-white/60 rounded-2xl border border-border/50" aria-label="Explore PDRN Skincare">
          <h2 className="text-lg font-serif font-semibold text-foreground mb-4">{t('seo.explorePDRN')}</h2>
          <div className="flex flex-wrap gap-3">
            <Link to="/blog/pdrn-serum-salmon-dna-benefits" className="text-sm text-primary hover:underline font-medium">{t('seo.anchor.serumBenefits')}</Link>
            <span className="text-border">•</span>
            <Link to="/blog/what-is-pdrn-salmon-dna-skincare" className="text-sm text-primary hover:underline font-medium">{t('seo.anchor.howPDRNWorks')}</Link>
            <span className="text-border">•</span>
            <Link to="/blog/how-to-achieve-glass-skin-with-pdrn" className="text-sm text-primary hover:underline font-medium">{t('seo.anchor.glassSkinGuide')}</Link>
            <span className="text-border">•</span>
            <Link to="/blog/korean-skincare-secret-pdrn" className="text-sm text-primary hover:underline font-medium">{t('seo.anchor.koreanSecret')}</Link>
            <span className="text-border">•</span>
            <Link to="/blog/pdrn-vs-traditional-skincare" className="text-sm text-primary hover:underline font-medium">{t('seo.anchor.pdrnVsRegular')}</Link>
          </div>
        </section>

        {/* Shipping Marquee */}
        <ShippingMarquee />

        {/* Product Disclaimer */}
        <p className="text-xs text-muted-foreground/70 leading-relaxed mt-4 mb-4 max-w-4xl mx-auto text-center">
          {t('product.disclaimer')}
        </p>
      </main>

      {/* Mobile Sticky Cart */}
      <MobileStickyCart product={product} handle={handle || ''} />

      <Footer />
    </div>
  );
}
